import { FC } from 'react';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { CustomChartPoint } from '@/entities/dashboards';
import { ChartCardShell } from '../ChartCardShell';
import { ChartPointTooltip } from './ChartPointTooltip';
import { CHART_AXIS_TICK, CHART_LINE_COLOR, CHART_SERIES_COLOR, formatAxisValue } from './chartAxis';

type Props = {
  title: string;
  points: CustomChartPoint[];
};

/** Ширина оси величины: хватает подписи с дробной частью, но не отнимает ширину у самой линии. */
const VALUE_AXIS_WIDTH = 44;

/** Величина отметки периода и её же величина под курсором. */
const POINT_SIZE = 3;
const ACTIVE_POINT_SIZE = 5;

/**
 * Линейный график: изменение величины по периодам.
 *
 * Точка без значения разрывает линию — соединять соседние периоды через пропуск нельзя, это
 * рисовало бы динамику, которой не было.
 *
 * Ось величины идёт от наименьшего значения ряда, а не от нуля: у показателей с узким размахом
 * (средний балл, доля) ход по периодам на шкале от нуля сплющился бы в прямую.
 */
export const LineChartView: FC<Props> = ({ title, points }) => (
  <ChartCardShell title={title}>
    <div className="h-48 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={points}>
          <CartesianGrid stroke={CHART_LINE_COLOR} strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="label"
            tick={CHART_AXIS_TICK}
            tickLine={false}
            axisLine={{ stroke: CHART_LINE_COLOR }}
            interval="preserveStartEnd"
          />
          <YAxis
            width={VALUE_AXIS_WIDTH}
            domain={['auto', 'auto']}
            tickFormatter={formatAxisValue}
            tick={CHART_AXIS_TICK}
            tickLine={false}
            axisLine={false}
          />
          <Tooltip content={<ChartPointTooltip />} cursor={{ stroke: CHART_LINE_COLOR }} />
          <Line
            type="linear"
            dataKey="value"
            stroke={CHART_SERIES_COLOR}
            dot={{ r: POINT_SIZE, fill: CHART_SERIES_COLOR, stroke: CHART_SERIES_COLOR }}
            activeDot={{ r: ACTIVE_POINT_SIZE }}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  </ChartCardShell>
);
